/* eslint-disable multiline-ternary */
import { Transition } from '@headlessui/react'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import React, { useState } from 'react'
import CloseIcon from '../icons/CloseIcon'
import QuestionMarkCircleIcon from '../icons/QuestionMarkCircleIcon'

function renderReadyText (seconds, timer) {
  if (!timer) {
    return 'Ready... Set... Go!'
  }

  if (seconds >= 63) {
    return 'Ready...'
  }

  if (seconds >= 61) {
    return 'Ready... Set...'
  }

  return 'Ready... Set... Go!'
}

function renderTimerText (seconds, timer, exercise) {
  if (!timer) {
    return exercise.time
  }

  const s = seconds > 60 ? 60 : seconds

  return `Timer: ${s}`
}

const Exercise = ({
  active,
  color,
  exercise,
  position,
  seconds,
  step,
  timer,
  type
}) => {
  const [showModal, setShowModel] = useState(false)

  const bgClass = `bg-${color}`
  const imagePath01 = `svg/${type}-${exercise.image}-01.svg`
  const imagePath02 = `svg/${type}-${exercise.image}-02.svg`
  const textClass = `text-${color}`

  return (
    <div
      className={classNames('duration-300 ease-in-out flex flex-col h-full relative transition w-full lg:flex lg:w-1/3', {
        [bgClass]: active,
        'bg-white hidden': !active,
        'md:border-black md:border-l md:border-r': position === 'center'
      })}
    >
      <div className='flex flex-shrink-0 h-14 justify-between relative z-20 sm:h-16 md:h-20'>
        <h1
          className={classNames('flex items-center px-6 rounded-br-2xl text-2xl sm:text-3xl', {
            [`bg-white ${textClass}`]: active,
            [`${bgClass} text-white`]: !active
          })}
        >
          {step === 4 ? 'Cool Down' : step === 3 ? 'Move' : 'Warm-Up'}
        </h1>
        {active && (
          <div className='flex items-center px-2'>
            <button
              className='border border-transparent flex h-10 items-center justify-center rounded-full text-white w-10 hover:bg-opacity-25 hover:bg-white focus:outline-none sm:h-12 sm:w-12'
              type='button'
              onClick={() => setShowModel(s => !s)}
            >
              <div className='pulse rounded-full'>
                {showModal ? (
                  <CloseIcon className='block fill-current h-5 w-5' />
                ) : (
                  <QuestionMarkCircleIcon
                    className='block fill-current h-8 w-8'
                  />
                )}
              </div>
            </button>
          </div>
        )}
      </div>
      <div className='flex flex-1 flex-col overflow-hidden relative z-10'>
        <div className='flex flex-1 overflow-hidden p-6 md:p-24 lg:p-6'>
          <img
            className={classNames('h-auto object-contain w-full', { 'opacity-75': !active })}
            src={active ? imagePath02 : imagePath01}
            alt={exercise.name}
          />
        </div>
        <div className='flex-shrink-0 overflow-hidden p-6'>
          <h2
            className={classNames('duration-300 ease-in-out leading-tight text-3xl transform transition-transform lg:text-4xl', {
              [`${textClass} translate-x-2/4`]: !active,
              'text-white translate-x-0': active
            })}
          >
            <span
              className={classNames('duration-300 ease-in-out inline-block transform transition-transform', {
                '-translate-x-1/2 text-center': !active,
                'translate-x-0 text-left': active
              })}
            >
              {exercise.name}
            </span>
          </h2>
          <div className={classNames('mt-5', { invisible: !active })}>
            <div className='flex'>
              <div className='bg-white px-4 py-1 rounded-lg text-2xl text-green-400 lg:text-3xl'>
                <span>{renderTimerText(seconds, timer, exercise)}</span>
              </div>
            </div>
            <div className='leading-tight mt-5 text-2xl text-white lg:text-3xl'>
              <span>{renderReadyText(seconds, timer)}</span>
            </div>
          </div>
          <Transition
            className='absolute inset-0 overflow-hidden transform transition-all z-10'
            role='dialog'
            aria-labelledby='modal-headline'
            aria-modal='true'
            show={active && showModal}
            enter='ease-out duration-300'
            enterFrom='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
            enterTo='opacity-100 translate-y-0 sm:scale-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100 translate-y-0 sm:scale-100'
            leaveTo='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
          >
            <div className='h-full p-6 w-full'>
              <div className='bg-white flex flex-col h-full p-6 shadow-md w-full'>
                <div className='flex flex-shrink-0 items-center'>
                  <div className='flex items-center justify-center p-2 rounded-lg'>
                    <img
                      className='block h-auto w-20'
                      src={active ? imagePath01 : imagePath02}
                      alt={exercise.name}
                    />
                  </div>
                  <h3 className={classNames('text-xl leading-6 ml-6', textClass)}>
                    {exercise.name}
                  </h3>
                </div>
                <div className='flex flex-1 overflow-y-auto pt-6'>
                  <div>
                    <p className='font-poppins-medium leading-normal text-lg'>
                      {exercise.steps.join(' ')}
                    </p>
                    {exercise.desc && (
                      <p className={classNames('leading-normal mt-6 text-base', textClass)}>*{exercise.desc}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </div>
  )
}

Exercise.propTypes = {
  active: PropTypes.bool.isRequired,
  color: PropTypes.string.isRequired,
  exercise: PropTypes.object.isRequired,
  position: PropTypes.string.isRequired,
  seconds: PropTypes.number.isRequired,
  step: PropTypes.number.isRequired,
  timer: PropTypes.bool.isRequired,
  type: PropTypes.string.isRequired
}

export default Exercise
